import pluralize from "pluralize";

import { type QualificationCount } from "./api/schemas";

const MINIMUM_COUNT_FOR_SIGNIFICANT_ACTIVITY = 1;
const MAX_QUALIFICATIONS_TO_DISPLAY = 3;
const APPROXIMATE_COUNT_THRESHOLD = 100;

export interface BookingStatsCardProps {
  activityLabel: string;
  status: "success" | "warning";
  bodyMessage: string;
  footerMessage?: string;
  hasMatchingActivity: boolean;
}

function formatCount(count: number): string {
  if (count > APPROXIMATE_COUNT_THRESHOLD) {
    return `over ${APPROXIMATE_COUNT_THRESHOLD}`;
  }

  return String(count);
}

function formatQualificationEntry(entry: QualificationCount): string {
  const count = formatCount(entry.count);
  return `${count} ${entry.qualification} ${pluralize("shift", entry.count)}`;
}

function formatQualificationList(
  qualificationCounts: QualificationCount[],
  hasMoreQualifications: boolean
): string {
  if (qualificationCounts.length === 0) {
    return "";
  }

  if (qualificationCounts.length === 1) {
    return formatQualificationEntry(qualificationCounts[0]);
  }

  const parts = qualificationCounts.map((entry) => formatQualificationEntry(entry));

  if (hasMoreQualifications) {
    return `${parts.join(", ")}, and more`;
  }

  const allButLast = parts.slice(0, -1).join(", ");

  return `${allButLast} and ${parts[parts.length - 1]}`;
}

function getTotalCount(qualificationCounts: QualificationCount[]): number {
  return qualificationCounts.reduce((sum, entry) => sum + entry.count, 0);
}

export function getBookingStatsCardProps(params: {
  qualificationCounts: QualificationCount[];
  totalCount: number;
  workerQualifications: string[];
}): BookingStatsCardProps {
  const { qualificationCounts, totalCount, workerQualifications } = params;

  if (totalCount === 0) {
    return {
      activityLabel: "Limited shift activity",
      status: "warning",
      bodyMessage: "There hasn't been much shift activity in your area recently",
      hasMatchingActivity: false,
    };
  }

  const workerQualificationSet = new Set(
    workerQualifications.map((qualification) => qualification.toLowerCase())
  );

  const matchingCounts = qualificationCounts
    .filter((qualificationCount) =>
      workerQualificationSet.has(qualificationCount.qualification.toLowerCase())
    )
    .sort((a, b) => b.count - a.count);

  const hasSignificantMatchingCounts = matchingCounts.some(
    (qualificationCount) => qualificationCount.count >= MINIMUM_COUNT_FOR_SIGNIFICANT_ACTIVITY
  );

  if (hasSignificantMatchingCounts) {
    const displayCounts = matchingCounts.slice(0, MAX_QUALIFICATIONS_TO_DISPLAY);
    const hasMoreQualifications = matchingCounts.length > MAX_QUALIFICATIONS_TO_DISPLAY;
    const qualificationCountsList = formatQualificationList(displayCounts, hasMoreQualifications);
    const total = getTotalCount(displayCounts);
    const verb = total === 1 ? "was" : "were";

    return {
      activityLabel: "Current shift activity",
      status: "success",
      bodyMessage: `${qualificationCountsList} ${verb} booked recently in your area`,
      footerMessage: "New shifts are posted regularly — keep checking back!",
      hasMatchingActivity: true,
    };
  }

  const bodyMessage = "None of the shifts booked in your area matched your qualifications";

  return {
    activityLabel: "Current shift activity",
    status: "success",
    bodyMessage,
    hasMatchingActivity: false,
  };
}
